import React from 'react';

/**
 * AnalyticsChart - Simple line chart for displaying time-series data
 *
 * Displays page views and bookings over time in a dual-line chart
 * with no external dependencies (pure SVG implementation).
 */
const AnalyticsChart = ({ data, width = 800, height = 300 }) => {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-400 dark:text-silver/40">
        <p className="font-mono text-sm">No data available for the selected period</p>
      </div>
    );
  }

  const padding = { top: 20, right: 40, bottom: 40, left: 50 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  // Find max values for scaling
  const maxViews = Math.max(...data.map(d => d.views), 1);
  const maxBookings = Math.max(...data.map(d => d.bookings), 1);
  const maxValue = Math.max(maxViews, maxBookings);

  // Calculate x-axis step
  const xStep = chartWidth / (data.length - 1 || 1);

  // Generate view points
  const viewPoints = data.map((d, i) => {
    const x = padding.left + i * xStep;
    const y = padding.top + chartHeight - (d.views / maxValue) * chartHeight;
    return `${x},${y}`;
  }).join(' ');

  // Generate booking points
  const bookingPoints = data.map((d, i) => {
    const x = padding.left + i * xStep;
    const y = padding.top + chartHeight - (d.bookings / maxValue) * chartHeight;
    return `${x},${y}`;
  }).join(' ');

  // Format date labels
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const day = date.getDate();
    return `${month} ${day}`;
  };

  // Determine how many labels to show based on data length
  const labelInterval = data.length > 14 ? Math.floor(data.length / 7) : data.length > 7 ? 2 : 1;

  // Y-axis ticks
  const yTicks = 5;
  const yTickValues = Array.from({ length: yTicks }, (_, i) =>
    Math.round((maxValue / (yTicks - 1)) * i)
  );

  return (
    <div className="w-full overflow-x-auto">
      <svg width={width} height={height} className="font-mono text-xs">
        {/* Background */}
        <rect
          x={padding.left}
          y={padding.top}
          width={chartWidth}
          height={chartHeight}
          fill="transparent"
          stroke="currentColor"
          strokeWidth="1"
          className="text-slate-200 dark:text-silver/20"
        />

        {/* Grid lines */}
        {yTickValues.map((value, i) => {
          const y = padding.top + chartHeight - (value / maxValue) * chartHeight;
          return (
            <g key={i}>
              <line
                x1={padding.left}
                y1={y}
                x2={padding.left + chartWidth}
                y2={y}
                stroke="currentColor"
                strokeWidth="1"
                strokeDasharray="2,2"
                className="text-slate-200 dark:text-silver/10"
              />
              <text
                x={padding.left - 10}
                y={y + 4}
                textAnchor="end"
                className="fill-slate-400 dark:fill-silver/40"
              >
                {value}
              </text>
            </g>
          );
        })}

        {/* X-axis labels */}
        {data.map((d, i) => {
          if (i % labelInterval !== 0 && i !== data.length - 1) return null;
          const x = padding.left + i * xStep;
          return (
            <text
              key={i}
              x={x}
              y={padding.top + chartHeight + 20}
              textAnchor="middle"
              className="fill-slate-400 dark:fill-silver/40"
            >
              {formatDate(d.date)}
            </text>
          );
        })}

        {/* Views line (blue) */}
        <polyline
          points={viewPoints}
          fill="none"
          stroke="#3b82f6"
          strokeWidth="2"
          strokeLinejoin="round"
        />

        {/* Views points */}
        {data.map((d, i) => {
          const x = padding.left + i * xStep;
          const y = padding.top + chartHeight - (d.views / maxValue) * chartHeight;
          return (
            <circle
              key={i}
              cx={x}
              cy={y}
              r="3"
              fill="#3b82f6"
              className="hover:r-5 transition-all cursor-pointer"
            >
              <title>{`${formatDate(d.date)}: ${d.views} views`}</title>
            </circle>
          );
        })}

        {/* Bookings line (green) */}
        <polyline
          points={bookingPoints}
          fill="none"
          stroke="#10b981"
          strokeWidth="2"
          strokeLinejoin="round"
        />

        {/* Bookings points */}
        {data.map((d, i) => {
          const x = padding.left + i * xStep;
          const y = padding.top + chartHeight - (d.bookings / maxValue) * chartHeight;
          return (
            <circle
              key={i}
              cx={x}
              cy={y}
              r="3"
              fill="#10b981"
              className="hover:r-5 transition-all cursor-pointer"
            >
              <title>{`${formatDate(d.date)}: ${d.bookings} bookings`}</title>
            </circle>
          );
        })}

        {/* Legend */}
        <g transform={`translate(${padding.left + chartWidth - 150}, ${padding.top - 10})`}>
          <line x1="0" y1="0" x2="20" y2="0" stroke="#3b82f6" strokeWidth="2" />
          <text x="25" y="4" className="fill-slate-600 dark:fill-silver/60">Profile Views</text>

          <line x1="0" y1="15" x2="20" y2="15" stroke="#10b981" strokeWidth="2" />
          <text x="25" y="19" className="fill-slate-600 dark:fill-silver/60">Bookings</text>
        </g>
      </svg>
    </div>
  );
};

export default AnalyticsChart;
